import { useState, useEffect, useCallback } from "react";
import api, { getApiErrorMessage } from "../../lib/api";
import { useRewardCatalog } from "../../hooks/useRewardCatalog";
import CatalogPagination from "../../components/CatalogPagination";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { confirmToast } from "../../lib/confirmToast";
import { formatPoints, formatRewardType } from "../../lib/display";
import { compressImageToDataUrl } from "../../lib/images";
import RewardPhoto from "../../components/RewardPhoto";
import { useAuth } from "../../contexts/AuthContext";
import { Ban, Plus, Pencil, Gift, Search, Loader2, Coins, CreditCard, Phone, ShoppingBag, Ticket, ImagePlus, X, Store, RefreshCw, RotateCcw } from "lucide-react";

const typeIcons = {
  PIX: CreditCard,
  recarga: Phone,
  brinde: Gift,
  produto: ShoppingBag,
  cupom: Ticket,
};

const getEmptyRewardFormData = () => ({
  loja_id: "",
  loja_ids: [],
  nome: "",
  descricao: "",
  tipo_recompensa: "PIX",
  pontos_necessarios: "",
  estoque: "999",
  valor_financeiro: "",
  foto_base64: null,
});

const AdminRecompensas = () => {
  const { isMaster } = useAuth();
  const [lojas, setLojas] = useState([]);
  const [tiposRecompensa, setTiposRecompensa] = useState([]);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [addingType, setAddingType] = useState(false);
  const [creatingType, setCreatingType] = useState(false);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  const [formData, setFormData] = useState(getEmptyRewardFormData);
  const [catalogStatus, setCatalogStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const catalog = useRewardCatalog({ admin: true, search });
  const { refresh: refreshCatalog } = catalog;
  const recompensas = catalog.itens;
  const erpReward = recompensas.find((reward) => reward.id === editing && reward.origem === "citel");

  const fetchData = useCallback(async (refresh = true) => {
    if (refresh) refreshCatalog();
    try {
      const [lojasRes, tiposRes, catalogRes] = await Promise.all([
        api.get("/lojas"),
        api.get("/tipos-recompensa"),
        api.get("/catalogo-erp/status").catch(() => ({ data: null })),
      ]);
      setLojas(lojasRes.data);
      setTiposRecompensa(tiposRes.data);
      setCatalogStatus(catalogRes.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao carregar dados."));
    }
  }, [refreshCatalog]);

  useEffect(() => {
    void fetchData(false);
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const lojaIds = Array.from(new Set([
        formData.loja_id,
        ...formData.loja_ids,
        ...(!isMaster && lojas.length === 1 ? [lojas[0].id] : []),
      ].filter(Boolean)));
      if (lojaIds.length === 0) {
        toast.error("Selecione ao menos uma loja para esta recompensa.");
        return;
      }

      const payload = {
        ...formData,
        loja_id: lojaIds[0],
        loja_ids: lojaIds,
        pontos_necessarios: parseFloat(formData.pontos_necessarios),
        estoque: parseInt(formData.estoque),
        valor_financeiro: parseFloat(formData.valor_financeiro) || 0,
      };

      if (editing) {
        await api.put(`/recompensas/${editing}`, payload);
        toast.success("Recompensa atualizada com sucesso!");
      } else {
        await api.post("/recompensas", payload);
        toast.success("Recompensa criada com sucesso!");
      }
      setDialogOpen(false);
      setEditing(null);
      setFormData(getEmptyRewardFormData());
      setAddingType(false);
      setNewTypeName("");
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao salvar recompensa."));
    }
  };

  const handleCreateType = async () => {
    const nome = newTypeName.trim();
    if (!nome) {
      toast.error("Informe o nome do novo tipo.");
      return;
    }

    setCreatingType(true);
    try {
      const selectedLojaId = formData.loja_id || formData.loja_ids[0] || (!isMaster && lojas.length === 1 ? lojas[0].id : "");
      if (!isMaster && !selectedLojaId) {
        toast.error("Selecione uma loja antes de cadastrar um tipo.");
        return;
      }
      const response = await api.post("/tipos-recompensa", { nome, loja_id: selectedLojaId || null });
      setTiposRecompensa((current) => [...current, response.data].sort((a, b) => a.nome.localeCompare(b.nome)));
      setFormData((current) => ({ ...current, tipo_recompensa: response.data.codigo }));
      setNewTypeName("");
      setAddingType(false);
      toast.success("Tipo de recompensa criado!");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao criar tipo de recompensa."));
    } finally {
      setCreatingType(false);
    }
  };

  const handleEdit = (recompensa) => {
    const rewardLojaIds = Array.from(new Set([recompensa.loja_id, ...(recompensa.loja_ids || [])].filter(Boolean)));
    const lojaIds = rewardLojaIds.length > 0
      ? rewardLojaIds
      : isMaster
        ? lojas.map((loja) => loja.id)
        : [];
    setEditing(recompensa.id);
    setFormData({
      loja_id: lojaIds[0] || "",
      loja_ids: lojaIds,
      nome: recompensa.nome,
      descricao: recompensa.descricao || "",
      tipo_recompensa: recompensa.tipo_recompensa,
      pontos_necessarios: recompensa.pontos_necessarios.toString(),
      estoque: recompensa.estoque.toString(),
      valor_financeiro: (recompensa.valor_financeiro || 0).toString(),
      foto_base64: recompensa.foto_base64 || null,
    });
    setDialogOpen(true);
  };

  const toggleStore = (lojaId) => {
    setFormData((current) => {
      const lojaIds = current.loja_ids.includes(lojaId)
        ? current.loja_ids.filter((id) => id !== lojaId)
        : [...current.loja_ids, lojaId];
      return { ...current, loja_ids: lojaIds, loja_id: lojaIds[0] || "" };
    });
  };

  const toggleAllStores = () => {
    setFormData((current) => {
      const allSelected = lojas.length > 0 && lojas.every((loja) => current.loja_ids.includes(loja.id));
      const lojaIds = allSelected ? [] : lojas.map((loja) => loja.id);
      return { ...current, loja_ids: lojaIds, loja_id: lojaIds[0] || "" };
    });
  };

  const getRewardStoresLabel = (recompensa) => {
    const lojaIds = Array.from(new Set([recompensa.loja_id, ...(recompensa.loja_ids || [])].filter(Boolean)));
    if (lojaIds.length === 0) return "Todas as lojas (cadastro anterior)";
    const names = lojaIds.map((id) => lojas.find((loja) => loja.id === id)?.nome).filter(Boolean);
    return names.length > 0 ? names.join(", ") : `${lojaIds.length} ${lojaIds.length === 1 ? "loja" : "lojas"}`;
  };

  const handlePhotoChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setPhotoLoading(true);
    try {
      const foto_base64 = await compressImageToDataUrl(file);
      setFormData((current) => ({ ...current, foto_base64 }));
      toast.success("Foto adicionada à recompensa.");
    } catch (error) {
      toast.error(error?.message || "Não foi possível carregar a foto.");
    } finally {
      event.target.value = "";
      setPhotoLoading(false);
    }
  };

  const removePhoto = () => {
    setFormData((current) => ({ ...current, foto_base64: null }));
  };

  const deleteRecompensa = async (id) => {
    try {
      await api.delete(`/recompensas/${id}`);
      toast.success("Recompensa desativada com sucesso!");
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erro ao desativar recompensa."));
    }
  };

  const handleDelete = (id) => {
    confirmToast({
      title: "Desativar recompensa?",
      description: "Ela deixará de aparecer como opção de resgate.",
      confirmLabel: "Desativar",
      variant: "danger",
      onConfirm: () => deleteRecompensa(id),
    });
  };

  const syncCatalog = async () => {
    setSyncing(true);
    try {
      const response = await api.post("/catalogo-erp/sincronizar");
      toast.success(response.data.message);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Não foi possível iniciar a sincronização."));
    } finally {
      setSyncing(false);
    }
  };

  const restoreERP = (reward) => confirmToast({
    title: "Restaurar dados do ERP?",
    description: "As personalizações de nome, descrição, tipo e foto serão removidas. A disponibilidade continuará sujeita ao ERP.",
    confirmLabel: "Restaurar",
    onConfirm: async () => {
      try {
        await api.post(`/recompensas/${reward.id}/restaurar-erp`);
        toast.success("Dados do ERP restaurados.");
        fetchData();
      } catch (error) {
        toast.error(getApiErrorMessage(error, "Não foi possível restaurar os dados do ERP."));
      }
    },
  });

  const rewardTypeLabels = tiposRecompensa.reduce((acc, tipo) => {
    acc[tipo.codigo] = tipo.nome;
    return acc;
  }, {});

  const getRewardTypeLabel = (codigo) => rewardTypeLabels[codigo] || formatRewardType(codigo);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-recompensas">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Recompensas</h1>
          <p className="text-slate-600 mt-1">Gerencie o catálogo de recompensas</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        {isMaster && <Button variant="outline" onClick={syncCatalog} disabled={syncing} data-testid="sync-catalog-btn">
          {syncing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Sincronizar ERP
        </Button>}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="btn-primary"
              onClick={() => {
                setEditing(null);
                setFormData({
                  ...getEmptyRewardFormData(),
                  loja_id: !isMaster && lojas.length === 1 ? lojas[0].id : "",
                  loja_ids: !isMaster && lojas.length === 1 ? [lojas[0].id] : [],
                });
                setAddingType(false);
                setNewTypeName("");
              }}
              data-testid="add-recompensa-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Recompensa
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[calc(100vh-2rem)] overflow-y-auto sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editing ? "Editar Recompensa" : "Nova Recompensa"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="foto_recompensa">Foto da recompensa</Label>
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-3">
                  {formData.foto_base64 ? (
                    <div className="relative">
                      <RewardPhoto
                        src={formData.foto_base64}
                        alt={formData.nome || "Foto da recompensa"}
                        className="aspect-[16/9] w-full rounded-lg"
                        imageClassName={erpReward ? "object-contain bg-white" : undefined}
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        onClick={removePhoto}
                        className="absolute right-2 top-2 h-8 w-8 border-white/80 bg-white/95 text-slate-700 shadow-sm hover:bg-red-50 hover:text-red-600"
                        aria-label="Remover foto"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <label
                      htmlFor="foto_recompensa"
                      className="flex min-h-36 cursor-pointer flex-col items-center justify-center rounded-lg border border-slate-200 bg-white px-4 py-6 text-center transition-colors hover:border-primary/40 hover:bg-primary/5"
                    >
                      {photoLoading ? (
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      ) : (
                        <ImagePlus className="h-8 w-8 text-primary" />
                      )}
                      <span className="mt-3 text-sm font-semibold text-slate-900">
                        {photoLoading ? "Preparando foto..." : "Adicionar foto"}
                      </span>
                      <span className="mt-1 text-xs text-slate-500">
                        JPG, PNG ou WebP
                      </span>
                    </label>
                  )}
                  <Input
                    id="foto_recompensa"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handlePhotoChange}
                    className="sr-only"
                    data-testid="recompensa-foto-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="nome">Nome da recompensa</Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  className="input-field"
                  placeholder="Ex: PIX R$ 50,00"
                  required
                  data-testid="recompensa-nome-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="descricao">Descrição</Label>
                <Textarea id="descricao" value={formData.descricao} onChange={(event) => setFormData({ ...formData, descricao: event.target.value })} rows={3} maxLength={10000} data-testid="recompensa-descricao-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tipo_recompensa">Tipo</Label>
                <select
                  id="tipo_recompensa"
                  value={formData.tipo_recompensa}
                  onChange={(e) => setFormData({ ...formData, tipo_recompensa: e.target.value })}
                  className="input-field w-full"
                  data-testid="recompensa-tipo-select"
                >
                  {tiposRecompensa.filter((tipo) => !erpReward || !["PIX", "recarga", "cupom"].includes(tipo.codigo)).map((tipo) => (
                    <option key={tipo.codigo} value={tipo.codigo}>
                      {tipo.nome}
                    </option>
                  ))}
                </select>
                <div className="space-y-2">
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-auto px-0 text-sm text-primary hover:bg-transparent hover:text-primary/80"
                    onClick={() => setAddingType((current) => !current)}
                  >
                    {addingType ? "Cancelar novo tipo" : "Cadastrar novo tipo"}
                  </Button>
                  {addingType && (
                    <div className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:flex-row">
                      <Input
                        value={newTypeName}
                        onChange={(e) => setNewTypeName(e.target.value)}
                        className="input-field h-10 bg-white"
                        placeholder="Ex: Vale-compras"
                        data-testid="novo-tipo-recompensa-input"
                      />
                      <Button
                        type="button"
                        onClick={handleCreateType}
                        disabled={creatingType}
                        className="btn-primary h-10 sm:w-auto"
                        data-testid="novo-tipo-recompensa-btn"
                      >
                        {creatingType ? <Loader2 className="h-4 w-4 animate-spin" /> : "Adicionar"}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="pontos_necessarios">Pontos necessários</Label>
                  <Input
                    id="pontos_necessarios"
                    readOnly={Boolean(erpReward)}
                    title={erpReward ? "Atualizado automaticamente pelo ERP" : undefined}
                    type="number"
                    step="0.01"
                    value={formData.pontos_necessarios}
                    onChange={(e) => setFormData({ ...formData, pontos_necessarios: e.target.value })}
                    className="input-field"
                    required
                    data-testid="recompensa-pontos-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="valor_financeiro">Valor interno (R$)</Label>
                  <Input
                    id="valor_financeiro"
                    readOnly={Boolean(erpReward)}
                    title={erpReward ? "Atualizado automaticamente pelo ERP" : undefined}
                    type="number"
                    step="0.01"
                    value={formData.valor_financeiro}
                    onChange={(e) => setFormData({ ...formData, valor_financeiro: e.target.value })}
                    className="input-field"
                    placeholder="0.00"
                    data-testid="recompensa-valor-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="estoque">Estoque</Label>
                <Input
                  id="estoque"
                  readOnly={Boolean(erpReward)}
                  title={erpReward ? "Estoque ERP descontado das reservas locais" : undefined}
                  type="number"
                  value={formData.estoque}
                  onChange={(e) => setFormData({ ...formData, estoque: e.target.value })}
                  className="input-field"
                  data-testid="recompensa-estoque-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Lojas participantes</Label>
                <div className="max-h-44 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
                  {lojas.length > 1 && (
                    <Label
                      htmlFor="recompensa-todas-lojas"
                      className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 font-medium text-slate-800 hover:bg-slate-50"
                    >
                      <Checkbox
                        id="recompensa-todas-lojas"
                        disabled={Boolean(erpReward)}
                        checked={lojas.length > 0 && lojas.every((loja) => formData.loja_ids.includes(loja.id))}
                        onCheckedChange={toggleAllStores}
                      />
                      Selecionar todas as lojas
                    </Label>
                  )}
                  {lojas.map((loja) => (
                    <Label
                      key={loja.id}
                      htmlFor={`recompensa-loja-${loja.id}`}
                      className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-slate-50"
                    >
                      <Checkbox
                        id={`recompensa-loja-${loja.id}`}
                        disabled={Boolean(erpReward)}
                        checked={formData.loja_ids.includes(loja.id)}
                        onCheckedChange={() => toggleStore(loja.id)}
                      />
                      <span className="min-w-0 flex-1 text-sm text-slate-700">{loja.nome}</span>
                    </Label>
                  ))}
                </div>
                <p className="text-xs text-slate-500">A recompensa ficará disponível para parceiros cuja loja fixa esteja selecionada.</p>
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button type="submit" className="btn-primary flex-1" data-testid="save-recompensa-btn">
                  {editing ? "Atualizar" : "Criar"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {catalogStatus && <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-l-2 border-blue-500 pl-3 text-sm text-slate-600" data-testid="catalog-status">
        <span>ERP: {catalogStatus.habilitado ? "Automático" : "Carga manual"}</span>
        <span>Conversão: {catalogStatus.conversao_configurada ? "Configurada" : "Pendente"}</span>
        <span>Tabela: {catalogStatus.tabela_preco || "Pendente"}</span>
        <Button variant="ghost" size="icon" onClick={fetchData} title="Atualizar status do catálogo" aria-label="Atualizar status do catálogo"><RefreshCw className="h-4 w-4" /></Button>
        {catalogStatus.lojas?.filter((store) => store.sincronizacao?.erro).map((store) => <span key={store.id} className="basis-full text-red-700">{store.nome}: {store.sincronizacao.erro}</span>)}
      </div>}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
        <Input
          placeholder="Buscar recompensas..."
          maxLength={120}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input-field pl-10"
          data-testid="search-recompensas-input"
        />
      </div>

      {/* Cards Grid */}
      <CatalogPagination {...catalog} onPageChange={catalog.setPage} anchor />
      {catalog.error ? <div role="alert" className="flex flex-wrap items-center gap-3 py-8 text-red-700">{catalog.error}<Button variant="outline" onClick={catalog.refresh}>Tentar novamente</Button></div> : catalog.loading ? (
        <div className="min-h-72 p-12 flex items-center justify-center" aria-label="Carregando recompensas">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {recompensas.map((recompensa) => {
            const Icon = typeIcons[recompensa.tipo_recompensa] || Gift;
            return (
              <div
                key={recompensa.id}
                className="bg-white rounded-2xl border border-slate-100 overflow-hidden hover:shadow-lg transition-all group"
              >
                <RewardPhoto
                  src={recompensa.foto_base64}
                  alt={recompensa.nome}
                  icon={Icon}
                  className="aspect-[16/9] w-full rounded-none"
                  imageClassName={recompensa.origem === "citel" ? "object-contain bg-white" : undefined}
                  iconClassName="h-10 w-10"
                />
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="h-14 w-14 rounded-xl bg-accent/10 flex items-center justify-center">
                      <Icon className="h-7 w-7 text-accent" />
                    </div>
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                      {getRewardTypeLabel(recompensa.tipo_recompensa)}
                    </span>
                  </div>
                  
                  <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">
                    {recompensa.nome}
                  </h3>
                  {recompensa.origem === "citel" && <div className="mb-3 space-y-1 text-xs text-slate-500">
                    <p>ERP · {recompensa.codigo_produto_erp} · Empresa {recompensa.codigo_empresa_erp}</p>
                    {recompensa.erp_desativada_localmente ? <p className="text-amber-700">Desativada na plataforma</p> : recompensa.erp_motivo_bloqueio && <p className="text-amber-700">{recompensa.erp_motivo_bloqueio}</p>}
                    {recompensa.erp_campos_personalizados?.length > 0 && <p>Personalizada</p>}
                  </div>}
                  {recompensa.descricao && recompensa.descricao !== recompensa.nome && <p className="mb-3 line-clamp-2 text-sm text-slate-600">{recompensa.descricao}</p>}
                  
                  <div className="flex items-center gap-2 mb-4">
                    <Coins className="h-5 w-5 text-yellow-500" />
                    <span className="text-2xl font-heading font-bold text-slate-900">
                      {formatPoints(recompensa.pontos_necessarios)}
                    </span>
                    <span className="text-slate-500">pontos</span>
                  </div>

                  {recompensa.valor_financeiro > 0 && (
                    <p className="text-sm text-slate-500 mb-2">
                      Valor interno: R$ {recompensa.valor_financeiro.toFixed(2)}
                    </p>
                  )}
                  
                  <p className="text-sm text-slate-500">
                    Estoque: {recompensa.origem !== "citel" && recompensa.estoque > 900 ? "Ilimitado" : recompensa.estoque}
                  </p>
                  {recompensa.origem === "citel" && <p className="mt-1 text-xs text-slate-500">ERP: {recompensa.erp_estoque} · Reservados: {recompensa.erp_reservas}</p>}
                  <div className="mt-2 flex items-start gap-2 text-sm text-slate-500">
                    <Store className="mt-0.5 h-4 w-4 shrink-0" />
                    <span className="line-clamp-2">{getRewardStoresLabel(recompensa)}</span>
                  </div>

                  <div className="flex gap-2 pt-4 mt-4 border-t border-slate-100">
                    {recompensa.origem === "citel" && <Button variant="ghost" size="icon" onClick={() => restoreERP(recompensa)} title="Restaurar dados do ERP" aria-label="Restaurar dados do ERP"><RotateCcw className="h-4 w-4" /></Button>}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(recompensa)}
                      className="flex-1 text-slate-600 hover:text-primary"
                      data-testid={`edit-recompensa-${recompensa.id}`}
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(recompensa.id)}
                      className="flex-1 text-slate-600 hover:text-red-500"
                      title="Desativar recompensa"
                      aria-label="Desativar recompensa"
                      data-testid={`delete-recompensa-${recompensa.id}`}
                    >
                      <Ban className="h-4 w-4 mr-1" />
                      Desativar
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
          {recompensas.length === 0 && (
            <div className="col-span-full text-center py-12 text-slate-500">
              {search ? "Nenhuma recompensa encontrada" : "Nenhuma recompensa cadastrada ainda"}
            </div>
          )}
        </div>
      )}
      <CatalogPagination {...catalog} onPageChange={catalog.setPage} />
    </div>
  );
};

export default AdminRecompensas;
